import {
  Button,
  FormLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ChargeType,
  CustomFieldContext,
  GetInvoiceDetailsQuery,
  InvoiceStatus,
} from "../../../graphql/generated";
import InfoBlock from "../../common/InfoBlock";
import { useTranslation } from "react-i18next";
import RoleGuard from "../../account/Access/RoleGuard";
import { AttachMoney, CallSplit, Refresh } from "@mui/icons-material";
import { useEffect, useState } from "react";
import ObjectID from "bson-objectid";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import ShipmentLink from "../../common/ShipmentLink";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import { refreshDependentRateBases } from "../../../utils/refreshDependentRateBases";
import DownloadSendInvoiceButtonsContainer from "../DownloadSendInvoiceButtons";
import InvoicePaymentsList from "../InvoicePaymentsList/InvoicePaymentsList";
import InvoicePaymentFormModal from "../../InvoicePaymentFormModal/InvoicePaymentFormModal";
import { formatCurrency } from "../../../utils/labels/formatCurrency";
import EnumSelect from "../../common/EnumSelect";
import invoiceStatusLabel from "../../../utils/labels/invoiceStatusLabel";
import { omitBy } from "lodash";
import InvoiceDetailsChargeTable from "./InvoiceDetailsChargeTable";
import BusinessEntityLink from "../../asset-management/BusinessEntityLink/BusinessEntityLink";
import { testIds } from "./InvoiceDetails.testIds";
import CustomFieldsFormContainer from "../../extensions/CustomFieldsForm";
import InvoiceSeparationModalContainer from "../InvoiceSeparationModal";

export type InvoiceDetailsData = GetInvoiceDetailsQuery["invoiceById"];

export type InvoiceDetailsProps = {
  invoice: InvoiceDetailsData;
  onSave?: (invoice: InvoiceDetailsData) => void;
  onRecalculate?: () => void;
  onSeparated?: () => void;
  saving?: boolean;
  recalculating?: boolean;
  error?: Error | null;
};

const InvoiceDetails: React.FC<InvoiceDetailsProps> = (props) => {
  const { t } = useTranslation("finance");
  const [localInvoice, setLocalInvoice] = useState<InvoiceDetailsData>(
    props.invoice,
  );

  const updateInvoice = (updates: Partial<InvoiceDetailsData>) => {
    setLocalInvoice((localInvoice) => ({
      ...localInvoice,
      ...updates,
    }));
  };

  // The send and close button is in a separate component
  // and may update the status of the invoice, we sync it
  useEffect(() => {
    updateInvoice({
      status: props.invoice.status,
    });
  }, [props.invoice.status]);

  // Separating orders changes the order list, so it has to be synced too
  useEffect(() => {
    updateInvoice({
      charges: props.invoice.charges,
      taxRate: props.invoice.taxRate,
      customFields: props.invoice.customFields,
      shipments: props.invoice.shipments,
    });
  }, [
    props.invoice.charges,
    props.invoice.taxRate,
    props.invoice.customFields,
    props.invoice.shipments,
  ]);

  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isSeparationModalOpen, setSeparationModalOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] =
    useState<NonNullable<InvoiceDetailsData["payments"]>[0] | null>(null);

  const defaultReadonlyStatuses = [
    InvoiceStatus.Sent,
    InvoiceStatus.Closed,
    InvoiceStatus.Cancelled,
    InvoiceStatus.Transferred,
    InvoiceStatus.Paid,
  ];

  const isReadonly = (statuses: InvoiceStatus[] = [...defaultReadonlyStatuses]) =>
    statuses.includes(props.invoice.status);

  return (
    <Stack spacing={2}>
      <ErrorMessage error={props.error} />
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <FormLabel>Status</FormLabel>
          <EnumSelect
            data-testid="invoice-details-status"
            enumObject={omitBy(InvoiceStatus, (v) =>
              [InvoiceStatus.Sent].includes(v),
            )}
            disabled={isReadonly()}
            value={localInvoice.status}
            onChange={(e, status) => {
              if (!status) {
                return;
              }
              updateInvoice({ status });
            }}
            optionLabel={invoiceStatusLabel}
          />
        </Stack>
        <DownloadSendInvoiceButtonsContainer invoiceId={props.invoice._id} />
        <Stack direction="row" spacing={1}>
          {(props.invoice.shipments?.length || 0) > 1 ? (
            <Button
              data-testid={testIds.invoiceDetailsSeparate}
              variant="outlined"
              onClick={() => setSeparationModalOpen(true)}
              disabled={isReadonly([...defaultReadonlyStatuses, InvoiceStatus.OnHold])}
              startIcon={<CallSplit />}
            >
              {t("separate", "Separate")}
            </Button>
          ) : null}
          <Button
            data-testid={testIds.invoiceDetailsRecalculate}
            variant="outlined"
            onClick={() => {
              if (props.onRecalculate) {
                props.onRecalculate();
              }
            }}
            disabled={isReadonly() || props.recalculating}
            startIcon={<Refresh />}
          >
            {t("recalculate", "Recalculate")}
          </Button>
          <Button
            data-testid={testIds.invoiceDetailsSave}
            variant="contained"
            onClick={() => {
              if (props.onSave) {
                props.onSave(localInvoice);
              }
            }}
            disabled={isReadonly() || props.saving}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <InfoBlock title={t("orders", "Orders")}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("orderNumber", "Order Number")}</TableCell>
              <TableCell>{t("deliveryDate", "Delivery Date")}</TableCell>
              <TableCell>{t("customer", "Customer")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {localInvoice.shipments?.map((shipment) => (
              <TableRow key={shipment._id}>
                <TableCell>
                  <ShipmentLink shipment={shipment} />
                </TableCell>
                <TableCell>
                  {shipment.route?.lastDropoffTime ||
                  shipment.trip?.lastDropoffTime
                    ? formatDateTime(
                        (shipment.route?.lastDropoffTime ||
                          shipment.trip?.lastDropoffTime)!,
                      )
                    : "N/A"}
                </TableCell>
                <TableCell>
                  <BusinessEntityLink entityDetails={shipment.customer} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </InfoBlock>
      <InfoBlock title={t("customFields", "Custom Fields")}>
        <CustomFieldsFormContainer
          context={CustomFieldContext.Invoice}
          customFields={localInvoice.customFields || []}
          disabled={isReadonly()}
          onChange={(customFields) => {
            if (isReadonly()) {
              return;
            }
            updateInvoice({ customFields });
          }}
        />
      </InfoBlock>
      {[
        ChargeType.Linehaul,
        ChargeType.Accessorial,
        ChargeType.Tax,
        ChargeType.OrderCancellation,
      ].map((chargeType) => (
        <InvoiceDetailsChargeTable
          key={chargeType}
          chargeType={chargeType}
          readonly={isReadonly()}
          charges={localInvoice.charges.filter(
            (c) => c.chargeType === chargeType,
          )}
          shipments={localInvoice.shipments || []}
          onChange={(charges) => {
            updateInvoice({
              charges: refreshDependentRateBases([
                ...localInvoice.charges.filter(
                  (c) => c.chargeType !== chargeType,
                ),
                ...charges,
              ]),
            });
          }}
        />
      ))}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoBlock
            title={t("payments", "Payments")}
            action={
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <RoleGuard roles={["Carrier Admin", "Manager", "Dispatcher"]}>
                  <Button
                    data-testid={testIds.invoiceDetailsTotals}
                    variant="outlined"
                    onClick={() => {
                      setPaymentToEdit(null);
                      setPaymentModalOpen(true);
                    }}
                    startIcon={<AttachMoney />}
                    id="add-payment-button"
                  >
                    {t("addPayment", "Add payment")}
                  </Button>
                </RoleGuard>
              </Stack>
            }
          >
            <InvoicePaymentsList
              payments={localInvoice.payments || []}
              onEdit={(paymentId) => {
                const payment = localInvoice.payments?.find(
                  (p) => p._id === paymentId,
                );
                if (payment) {
                  setPaymentToEdit(payment);
                  setPaymentModalOpen(true);
                }
              }}
              onDelete={(paymentId) => {
                updateInvoice({
                  payments: (localInvoice.payments || []).filter(
                    (p) => p._id !== paymentId,
                  ),
                });
              }}
            />
          </InfoBlock>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoBlock title="Totals">
            <TableContainer
              sx={{
                width: "100%",
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Total Linehaul</TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      {formatTotal(localInvoice.charges, [ChargeType.Linehaul])}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Total Accessorial</TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      {formatTotal(localInvoice.charges, [
                        ChargeType.Accessorial,
                      ])}
                    </TableCell>
                  </TableRow>
                  <TableRow></TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              sx={{
                width: "100%",
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Subtotal</TableCell>
                    <TableCell></TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      {formatTotal(localInvoice.charges, [
                        ChargeType.Linehaul,
                        ChargeType.Accessorial,
                      ])}
                    </TableCell>
                  </TableRow>
                  {localInvoice.charges
                    .filter((charge) => charge.chargeType === ChargeType.Tax)
                    .map((charge) => (
                      <TableRow>
                        <TableCell>{charge.label}</TableCell>
                        <TableCell
                          sx={{
                            textAlign: "right",
                          }}
                        >
                          {(
                            ((charge.unit * charge.rate) /
                              getTotal(localInvoice.charges, [
                                ChargeType.Linehaul,
                                ChargeType.Accessorial,
                              ])) *
                            100
                          ).toFixed(2)}
                          %
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "right",
                          }}
                        >
                          {formatCurrency(charge.unit * charge.rate)}
                        </TableCell>
                      </TableRow>
                    ))}

                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">TOTAL</Typography>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "bold",
                        }}
                      >
                        {formatTotal(localInvoice.charges)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </InfoBlock>
        </Grid>
      </Grid>

      <InvoiceSeparationModalContainer
        open={isSeparationModalOpen}
        invoiceId={props.invoice._id}
        shipments={props.invoice.shipments || []}
        onClose={() => setSeparationModalOpen(false)}
        onSeparated={() => {
          if (props.onSeparated) {
            props.onSeparated();
          }
        }}
      />

      <InvoicePaymentFormModal
        isOpen={isPaymentModalOpen}
        initialPayment={paymentToEdit}
        onClose={() => {
          setPaymentToEdit(null);
          setPaymentModalOpen(false);
        }}
        onSubmit={(payment) => {
          if (paymentToEdit) {
            updateInvoice({
              payments: localInvoice.payments?.map((p) =>
                p._id === paymentToEdit._id ? payment : p,
              ),
            });
          } else {
            updateInvoice({
              payments: [
                ...(localInvoice.payments || []),
                {
                  ...payment,
                  _id: new ObjectID().toHexString(),
                },
              ],
            });
          }
          setPaymentToEdit(null);
          setPaymentModalOpen(false);
        }}
      />
    </Stack>
  );
};

export default InvoiceDetails;

const getTotal = (
  charges: GetInvoiceDetailsQuery["invoiceById"]["charges"],
  chargeTypes: ChargeType[] = Object.values(ChargeType),
) => {
  return charges
    .filter((c) => chargeTypes.includes(c.chargeType || ChargeType.Linehaul))
    .map((charge) => charge.unit * charge.rate)
    .reduce((a, b) => a + b, 0);
};

const formatTotal = (...args: Parameters<typeof getTotal>) => {
  return formatCurrency(getTotal(...args));
};
